import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationActions } from 'Store/Notification/Notification.actions';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import {
  INotification,
  INotificationMultipleReadRequest
} from 'Data/interfaces/Notification/INotification';
import useHistoryPush from 'Hooks/useHistoryPush';
import { useQueryReadAllNotifications } from 'Hooks/notifications/useQueryReadAllNotifications';
import { useQueryCountNewNotifications } from 'Hooks/notifications/useQueryCountNewNotifications';
import { useQueryCountNewNotificationsObra } from 'Hooks/notifications/useQueryCountNewNotificationsObra';
import { useQueryCountNewNotificationsPersonal } from 'Hooks/notifications/useQueryCountNewNotificationsPersonal';
import { useQueryReadNotification } from 'Hooks/notifications/useQueryReadNotification';
import { useQueryReadMultipleNotifications } from 'Hooks/notifications/useQueryReadMultipleNotifications';
import SpinCustom from 'Components/UI/SpinCustom';
import Badge from 'Components/UI/Badge';
import { Icon } from 'Components/UI';
import Button from 'Components/UI/Button';
import ActionsContent from './components/ActionsContent';
import { ActiveTabType, TabsRef } from './components/Tabs';
import {
  PopoverStyled,
  NotificationButton,
  DropdownNotification,
  Header,
  Footer,
  Main,
} from './styles';

type NotificationListProps = {
  constructionSiteId: number | null;
  loadingExternalLink: boolean;
}

const NotificationList = ({
  constructionSiteId,
  loadingExternalLink,
}: NotificationListProps) => {
  const { t } = useTranslation('notification');
  const dispatch = useDispatch();
  const { historyPush } = useHistoryPush();
  const theme = useSelector(getTheme);

  const { data: allCount } = useQueryCountNewNotifications();
  const {
    data: allCountObra
  } = useQueryCountNewNotificationsObra(constructionSiteId);
  const {
    data: allCountPersonal
  } = useQueryCountNewNotificationsPersonal();
  const { readNotificationAsync, isLoading: isLoadingReadNotification } = useQueryReadNotification();
  const { readMultipleNotificationsAsync, isLoading: isLoadingReadMultipleNotifications } = useQueryReadMultipleNotifications();
  const { readAllNotifications, isLoading: isLoadingReadAllNotifications } = useQueryReadAllNotifications();

  const tabsRef = useRef<TabsRef>(null);
  const [open, setOpen] = useState(false);
  const [currentActiveTab, setCurrentActiveTab] = useState<ActiveTabType>('allConstructionSites');
  const [readingNotificationId, setReadingNotificationId] = useState<string | null>(null);

  const CountNotification = useMemo(() => {
    const showCount = constructionSiteId ? allCountObra : allCount;
    if (showCount <= 0) {
      return <Icon icon='notificacoes' customColor='#828282' customSize={24} />
    }

    return (
      <Badge value={showCount} type="circle">
        <Icon icon='notificacoes' customColor='#828282' customSize={24} />
      </Badge>
    );
  }, [allCount, allCountObra, constructionSiteId]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleRedirect = (url: string) => {
    historyPush(url);
    handleOpenChange(false);
  };

  const handleMarkAsRead = async (readRow: INotification) => {
    setReadingNotificationId(readRow.NotificationId);
    if (readRow?.AggNotificationIds && readRow.AggNotificationIds?.length > 0) {
      const notificationAsView: INotificationMultipleReadRequest = {
        constructionSiteId: readRow.ConstructionSiteFk,
        notificationIds: [readRow.NotificationId, ...readRow?.AggNotificationIds || []],
        itemAuxId: readRow.ItemAuxId,
      };
      await readMultipleNotificationsAsync(notificationAsView);
    } else {
      await readNotificationAsync({
        notificationId: readRow.NotificationId,
        constructionSiteId: readRow.ConstructionSiteFk,
        itemAuxId: readRow.ItemAuxId
      });
    }
    setReadingNotificationId(null);
  };

  const handleMarkAllAsRead = async () => {
    if (currentActiveTab === 'allConstructionSites') {
      readAllNotifications({});
    } else {
      readAllNotifications({
        constructionSiteId: constructionSiteId,
      });
    }
  };

  const seeAllNotifications = () => {
    if (constructionSiteId && currentActiveTab === 'constructionSite') {
      dispatch(NotificationActions.choseDefaultFilterObra([constructionSiteId]));
    }
    handleRedirect('/notification');
  };

  const showFooter = useMemo(() => {
    if (currentActiveTab === 'personal') return allCountPersonal > 0;
    if (currentActiveTab === 'allConstructionSites') return allCount > 0;
    return allCountObra > 0;
  }, [allCount, allCountObra, allCountPersonal, currentActiveTab]);

  const readNotificationLoading = isLoadingReadNotification ||
    isLoadingReadMultipleNotifications;

  const menu = (
    <DropdownNotification>
      <Header>
        <span className="title">{t('widget.title')}</span>
        <Icon
          icon="cancelar"
          className="closeIcon"
          onClick={() => handleOpenChange(false)}
        />
      </Header>

      <Main>
        <ActionsContent
          tabsRef={tabsRef}
          constructionSiteId={constructionSiteId}
          loadingExternalLink={loadingExternalLink}
          allCount={allCount}
          allCountObra={allCountObra}
          allCountPersonal={allCountPersonal}
          readingNotificationId={readingNotificationId}
          isLoadingReadNotification={readNotificationLoading}
          onRedirect={handleRedirect}
          onMarkAsRead={handleMarkAsRead}
          onTabChange={setCurrentActiveTab}
        />
      </Main>

      {showFooter && (
        <Footer>
          {currentActiveTab !== 'personal' && (
            <Button
              type="text"
              className="btnMarkAllAsRead"
              disabled={isLoadingReadAllNotifications}
              onClick={handleMarkAllAsRead}
            >
              {isLoadingReadAllNotifications ? (
                <SpinCustom
                  size={10}
                  color={theme.colors.secondary.onSecondary}
                  marginRigth={8}
                />
              ) : (
                <Icon
                  icon="doubleCheck"
                  customSize={16}
                  className="iconBtnMarkAllAsRead"
                />
              )}
              {t('widget.markAllAsRead')}
            </Button>
          )}
          <Button
            type="primary"
            className="btnViewAll"
            onClick={seeAllNotifications}
          >
            {t('widget.viewAll')}
          </Button>
        </Footer>
      )}
    </DropdownNotification>
  )

  return (
    // Conteúdo só existe com o painel aberto: montado escondido (display: none),
    // o Virtuoso mede altura 0 ao trocar de obra/aba e desenha 1 item ou nenhum
    // — painel em branco até fechar e abrir de novo.
    <PopoverStyled
      destroyTooltipOnHide
      content={menu}
      placement="bottomLeft"
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      overlayInnerStyle={{ padding: 0, borderRadius: 12 }}
      showArrow={false}
    >
      <NotificationButton>
        {CountNotification}
      </NotificationButton>
    </PopoverStyled>
  );
};

export default NotificationList;
